// PageDescription: 의약품 정보 상세 페이지 (키워드 포함)
import MedicineDictionaryDetailTemplate from '@/components/templates/medicine-dictionary/MedicineDictionaryDetailTemplate';
import { SIX_HOURS_IN_MILLISECONDS } from '@/constants/common';
import MedicineDictionaryModel from '@/entities/medicine-dictionary/model/medicineDictionary.model';
import { createMedicineDictionaryDetailUrl } from '@/entities/medicine-dictionary/util';
import useAppInstallTopBanner from '@/hooks/cuj/useAppInstallTopBanner';
import useMedicineDictionaryDetailViewModel from '@/hooks/medicine-dictionary/useMedicineDictionaryDetailViewModel';
import useMedicineDictionaryItemByPublicId from '@/hooks/medicine-dictionary/useMedicineDictionaryItemByPublicId';
import { CustomPageProps } from '@/types/app';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';

const createMedicineDictionaryDetailLdJson = (medicineDetail?: MedicineDictionaryModel) => {
  if (!medicineDetail) {
    return {};
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Drug',
    name: medicineDetail.getBasicInfoName(),
    nonProprietaryName: medicineDetail.getSimplifiedName(),
    activeIngredient: medicineDetail.getFormattedIngredientName(),
    isAvailableGenerically: medicineDetail.getEthicalType() === '일반의약품',
    isProprietary: medicineDetail.getEthicalType() === '전문의약품',
    prescriptionStatus:
      medicineDetail.getEthicalType() === '일반의약품'
        ? 'https://schema.org/OTC'
        : 'https://schema.org/PrescriptionOnly',
    warning: medicineDetail.getMedicineGuideCautions()?.join(', '),
    identifier: {
      '@type': 'PropertyValue',
      name: 'Medicine ID',
      description: 'DoctorNow Medicine Dictionary ID',
      value: medicineDetail.getPublicId(),
    },
    image: medicineDetail.getRepresentativeImage(),
    brand: {
      '@type': 'Organization',
      name: medicineDetail.getCompanyName(),
    },
    recognizingAuthority: {
      '@type': 'Organization',
      name: '식품의약품안전처',
      url: 'https://uvoice.mfds.go.kr',
    },
    description: medicineDetail.getPharmaceuticalEffectLiteracy(),
    url: `${process.env.NEXT_PUBLIC_DOMAIN}${createMedicineDictionaryDetailUrl(medicineDetail)}`,
  };
};

interface Props extends CustomPageProps {
  publicId: string;
  keyword: string;
}

const MedicineDictionaryDetailWithKeywordPage = ({ client, publicId }: Props) => {
  const viewModel = useMedicineDictionaryDetailViewModel({ client, publicId });
  const topBannerViewModel = useAppInstallTopBanner();
  const ldJson = createMedicineDictionaryDetailLdJson(viewModel.medicineDictionaryItemByPublicId);

  return (
    <>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      </Head>
      <NextSeo
        title={`${viewModel.medicineDictionaryItemByPublicId?.getBasicInfoName()} 효능/효과, 부작용, 복용법 - 닥터나우 의약품 사전`}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${createMedicineDictionaryDetailUrl(
          viewModel.medicineDictionaryItemByPublicId
        )}`}
        description={`${viewModel.medicineDictionaryItemByPublicId?.getPharmaceuticalEffectLiteracy()} ${viewModel.medicineDictionaryItemByPublicId?.getSimplifiedName()}. 효과부터 부작용, 복용법, 주의사항까지 ${viewModel.medicineDictionaryItemByPublicId?.getSimplifiedName()}에 대한 정보를 닥터나우 의약품 사전에서 확인해보세요.`}
      />
      <MedicineDictionaryDetailTemplate topBannerViewModel={topBannerViewModel} viewModel={viewModel} client={client} />
    </>
  );
};

export default MedicineDictionaryDetailWithKeywordPage;

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (context) => {
  try {
    const publicId = context.params?.id;
    const keyword = context.params?.keyword;

    if (!publicId || typeof publicId !== 'string' || !keyword || typeof keyword !== 'string') {
      return {
        notFound: true,
      };
    }

    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: SIX_HOURS_IN_MILLISECONDS,
        },
      },
    });

    await useMedicineDictionaryItemByPublicId.prefetchQuery(queryClient, publicId);

    const dehydratedState = dehydrate(queryClient);

    return {
      props: {
        publicId,
        keyword: keyword,
        dehydratedState: JSON.parse(JSON.stringify(dehydratedState)),
      },
    };
  } catch {
    return {
      props: {
        publicId: context.params?.id as string,
        keyword: context.params?.keyword as string,
        dehydratedState: { queries: [], mutations: [] },
      },
    };
  }
});
